import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import api from '../../../api/axios'
import BaseModal from '../../utils/BaseModal'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../store'
import { useCurrency } from '../../../hooks/useCurrency'
import { setLoading, setRate } from '../../../store/slices/currencySlice'
import { rateFromScaled } from '../../utils/UnitConversion.utils'
import { useDepositWithdrawModal } from './DepositWithdrawModalContext'
import Deposit from './deposit/Deposit'
import Withdraw from './withdraw/Withdraw'
import { SUPPORTED_COUNTRIES } from './countries'

export type PayoutMethodResponseDto = {
  id?: string
  methodType: string
  isDefault: boolean
  iban?: string
  bic?: string
  walletAddress?: string
  network?: string
  last4?: string
  brand?: string
}

export type ProviderFeeDTO = {
  feeType: 'CREDIT_CARD_EUR' | 'CREDIT_CARD_GLOBAL' | 'OPEN_BANKING'
  rateScaled: number
  fixedMinor: number
  minAmountMinor: number
  maxAmountMinor: number
  kycRequired?: boolean
  supportedCurrencies?: string[]
  supportedCountries?: string[]
}

export type TransactionDetailsDTO = {
  currencyRateScaled: number
  eurRateScaled: number
  firstName: string
  lastName: string
  dateOfBirth: string
  street1: string
  street2: string
  postalCode: string
  city: string
  country: string
  nationality: string
  region: string
  iban: string
  depositProviderFees: ProviderFeeDTO[]
  withdrawProviderFees: ProviderFeeDTO[]
  payoutMethods: PayoutMethodResponseDto[]
}

const presetAmountsDeposit = [25, 50, 100, 250, 500, 1000]

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  PLN: 'zł',
  CHF: 'CHF',
  SEK: 'kr',
  NOK: 'kr',
  DKK: 'kr',
  CZK: 'Kč',
  HUF: 'Ft',
  CAD: 'CA$',
  AUD: 'A$',
  JPY: '¥',
  CNY: '¥',
}

// Resolves currency symbol for a given ISO code with Intl fallback
export const getCurrencySymbol = (currencyCode?: string | null): string => {
  if (!currencyCode) return '$'
  if (CURRENCY_SYMBOLS[currencyCode]) return CURRENCY_SYMBOLS[currencyCode]
  try {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
    const parts = formatter.formatToParts(0)
    const symbolPart = parts.find(p => p.type === 'currency')
    return symbolPart ? symbolPart.value : currencyCode
  } catch {
    return currencyCode
  }
}

type ModalHeaderProps = {
  modalMode: 'deposit' | 'withdraw'
  openModal: (mode: 'deposit' | 'withdraw') => void
}

const ModalHeader = React.memo(({ modalMode, openModal }: ModalHeaderProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const depositRef = useRef<HTMLButtonElement>(null)
  const withdrawRef = useRef<HTMLButtonElement>(null)
  const [lineStyle, setLineStyle] = useState({ width: 0, left: 0 })

  const updateLine = () => {
    const activeRef = modalMode === 'deposit' ? depositRef.current : withdrawRef.current
    const container = containerRef.current
    if (!activeRef || !container) return
    const containerRect = container.getBoundingClientRect()
    const activeRect = activeRef.getBoundingClientRect()
    setLineStyle({ width: activeRect.width, left: activeRect.left - containerRect.left })
  }

  useLayoutEffect(() => { updateLine() }, [modalMode])
  useEffect(() => { window.addEventListener('resize', updateLine); return () => window.removeEventListener('resize', updateLine) }, [])

  return (
      <div ref={containerRef} className="relative flex w-full gap-6 border-b border-white/10">
        <button ref={depositRef} onClick={() => openModal('deposit')} className={`pb-2 text-sm font-semibold transition-colors ${modalMode === 'deposit' ? 'text-white' : 'text-gray-400 hover:text-white'}`}>
          Deposit
        </button>
        <button ref={withdrawRef} onClick={() => openModal('withdraw')} className={`pb-2 text-sm font-semibold transition-colors ${modalMode === 'withdraw' ? 'text-white' : 'text-gray-400 hover:text-white'}`}>
          Withdraw
        </button>
        <div className="absolute bottom-0 h-[2px] bg-dark-primary rounded-full transition-all duration-300 ease-out" style={{ width: `${lineStyle.width}px`, transform: `translateX(${lineStyle.left}px)` }} />
      </div>
  )
})

ModalHeader.displayName = 'ModalHeader'

const DepositWithdrawModal = () => {
  const dispatch = useDispatch()
  const { modalOpen, modalMode, closeModal, openModal } = useDepositWithdrawModal()
  const { formatAmountWithoutCurrency, convertFromUSD: reduxConvertFromUSD, currency } = useCurrency()
  const { rate: reduxRate } = useSelector((state: RootState) => state.currency)
  const kyc = useSelector((state: RootState) => state.auth.kycStatus)

  const [transactionDetails, setTransactionDetails] = useState<TransactionDetailsDTO | null>(null)
  const [provider, setProvider] = useState<string | null>(null)
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null)

  useEffect(() => {
    if (!modalOpen) return
    setProvider(null)
  }, [modalOpen, modalMode])

  // Determine target currency derived from country selection or fallback to Redux state
  const targetCurrency = useMemo(() => {
    if (!selectedCountry) return currency
    const countryObj = SUPPORTED_COUNTRIES.find(
        (c) => c.code.toLowerCase() === selectedCountry.toLowerCase() || c.enumName === selectedCountry
    )
    return countryObj?.currency || currency
  }, [selectedCountry, currency])

  // Compute symbol matching selected country currency
  const targetSymbol = useMemo(() => {
    return getCurrencySymbol(targetCurrency)
  }, [targetCurrency])

  useEffect(() => {
    if (!modalOpen) return

    const fetchDetails = async () => {
      try {
        dispatch(setLoading(true))
        const { data } = await api.get<TransactionDetailsDTO>('/payment-provider/details', {
          params: { currency: targetCurrency, country: selectedCountry }
        })

        const fetchedRate = rateFromScaled(data.currencyRateScaled)

        // Refresh global Redux rate only when country currency matches global currency
        if (targetCurrency === currency) {
          dispatch(setRate(fetchedRate))
        }

        // Initialize country state if returned from transaction details
        if (!selectedCountry && data.country) {
          setSelectedCountry(data.country)
        }

        setTransactionDetails(data)
        dispatch(setLoading(false))
      } catch {
        dispatch(setLoading(false))
      }
    }

    fetchDetails()
  }, [modalOpen, currency, targetCurrency, selectedCountry, dispatch])

  // Active rate derived from backend response or Redux fallback
  const activeRate = useMemo(() => {
    return transactionDetails?.currencyRateScaled
        ? rateFromScaled(transactionDetails.currencyRateScaled)
        : reduxRate
  }, [transactionDetails, reduxRate])

  // Convert USD to active target country currency using active exchange rate
  const convertFromUSD = (usdAmount: number) => {
    return usdAmount * activeRate
  }

  const handleOpenAddMethodModal = async () => {
    try {
      const { data } = await api.post<{ onboardingUrl: string }>('/stripe/connect/add-method')
      if (data?.onboardingUrl) {
        window.location.href = data.onboardingUrl
      }
    } catch (error) {
      console.error('Error creating update link', error)
    }
  }

  const header = useMemo(() => <ModalHeader modalMode={modalMode} openModal={openModal} />, [modalMode, openModal])

  return (
      <BaseModal open={modalOpen} onAbort={closeModal} onSubmit={closeModal} maxWidth="md" minHeight="93vh" maxHeight="93vh" titleNode={header}>
        <div className="overflow-hidden w-full h-full flex flex-col flex-1">
          <div className="flex w-[200%] h-full flex-1 transition-transform duration-300 ease-out" style={{ transform: modalMode === 'deposit' ? 'translateX(0%)' : 'translateX(-50%)' }}>
            <div className="w-1/2 shrink-0 h-full flex flex-col flex-1">
              <Deposit
                  presetAmounts={presetAmountsDeposit}
                  currency={targetCurrency}
                  symbol={targetSymbol}
                  rate={activeRate}
                  eurRateScaled={transactionDetails?.eurRateScaled ?? 1}
                  convertFromUSD={convertFromUSD}
                  formatAmountWithoutCurrency={formatAmountWithoutCurrency}
                  provider={provider}
                  setProvider={setProvider}
                  selectedCountry={selectedCountry}
                  setSelectedCountry={setSelectedCountry}
                  closeModal={closeModal}
                  kyc={kyc ?? 'PENDING'}
              />
            </div>
            <div className="w-1/2 shrink-0 h-full flex flex-col flex-1">
              {transactionDetails?.withdrawProviderFees && (
                  <Withdraw
                      transactionDetails={transactionDetails}
                      currency={targetCurrency}
                      symbol={targetSymbol}
                      rate={activeRate}
                      convertFromUSD={convertFromUSD}
                      formatAmountWithoutCurrency={formatAmountWithoutCurrency}
                      selectedCountry={selectedCountry}
                      setSelectedCountry={setSelectedCountry}
                      closeModal={closeModal}
                      kyc={kyc ?? 'PENDING'}
                      onOpenAddMethodModal={handleOpenAddMethodModal}
                  />
              )}
            </div>
          </div>
        </div>
      </BaseModal>
  )
}

export default DepositWithdrawModal